import { FirebaseApp, initializeApp } from 'firebase/app';
import { browserSessionPersistence, getAuth, setPersistence } from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  serverTimestamp,
  setLogLevel,
  Timestamp,
} from 'firebase/firestore';
import { getQueryClient } from '@bridebook/web/store/react-query-client';
import { Cards } from '@bridebook/models/source/models/Countries/Cards';
import { getIsE2E } from '@bridebook/toolbox/src/e2e/get-is-e2e';
import { AutocompletePredictionsService } from '@bridebook/web/app-shared/lib/search/utils/autocomplete-predictions-service';
import { CustomPredictionService } from '@bridebook/web/app-shared/lib/search/utils/custom-autocomplete-predictions/custom-prediction-service';
import { GooglePlacesService } from '@bridebook/web/app-shared/lib/search/utils/google-places-service';
import { env } from '@bridebook/web/lib/env';
import { FeatureCardsService } from '@bridebook/web/lib/feature-cards/feature-cards-service';
import { CordovaTrackerService } from '@bridebook/web/lib/track-utils/services/cordova-tracker-service';
import { MuteActionsService } from '@bridebook/web/lib/utils/mute-actions-service';
import { WarningModalService } from '@bridebook/web/lib/utils/warning-modal-service';
import validate from './validate';
import { LostLeadService } from '@bridebook/web/lib/lost-lead/lost-lead-service';
import { getisIOS } from '@bridebook/web/lib/utils/get-is-ios';
import { AutocompleteSuggestionService } from '@bridebook/web/app-shared/lib/search/utils/autocomplete-suggestion-service';

const firebaseConfig = env.FIREBASE;

const getFirebaseDeps = (firebaseApp: FirebaseApp) => ({
  firebaseApp,
  serverTimestamp: serverTimestamp() as Timestamp,
});

export type IFirebaseDeps = ReturnType<typeof getFirebaseDeps>;

// Ensure only one Firebase instance.
let firebaseDeps: IFirebaseDeps | null = null;

const createFirebaseDeps = () => {
  if (!firebaseDeps && typeof window !== 'undefined') {
    const firebaseApp = initializeApp(firebaseConfig);
    firebaseDeps = getFirebaseDeps(firebaseApp);

    const isIOS = getisIOS();

    initializeFirestore(firebaseApp, {
      experimentalAutoDetectLongPolling: true,
      experimentalLongPollingOptions: {
        timeoutSeconds: 25,
      },
      ignoreUndefinedProperties: true,
      ...(!isIOS && {
        localCache: persistentLocalCache({
          cacheSizeBytes: 50 * 1024 ** 2, // 50MB
          tabManager: persistentMultipleTabManager(),
        }),
      }),
    });

    // When running inside a E2E test, the authentication information is saved
    // and retrieved from the session storage, instead of using the default
    // persistence in the browser IndexDB. This approach enables saving the
    // authentication data into a JSON file and reusing it between tests, which
    // is faster and doesn't hit the Firebase quota of logins.
    if (getIsE2E()) {
      const auth = getAuth();
      setPersistence(auth, browserSessionPersistence);
    }

    setLogLevel('error');
  }
  return firebaseDeps;
};

const fbDeps = createFirebaseDeps();
export const getFirebaseApp = () => fbDeps?.firebaseApp;

const muteActions = new MuteActionsService();
const warningModal = new WarningModalService(muteActions);
const cordovaTracker = new CordovaTrackerService(muteActions);
const googlePlacesService = new GooglePlacesService();
const customPredictionService = new CustomPredictionService();
const autocompletePredictionsService = new AutocompletePredictionsService(
  customPredictionService,
  googlePlacesService,
);
const autocompleteSuggestionService = new AutocompleteSuggestionService(
  customPredictionService,
  googlePlacesService,
);

const featureCardsService = new FeatureCardsService(Cards);
const lostLeadService = new LostLeadService();

const configureDeps = () => ({
  ...fbDeps,
  validate,
  firebaseUrl: firebaseConfig.databaseURL,
  cordovaTracker,
  muteActions,
  googlePlacesService,
  autocompletePredictionsService,
  autocompleteSuggestionService,
  featureCardsService,
  warningModal,
  queryClient: getQueryClient(),
  lostLeadService,
});

export type IConfigureDeps = ReturnType<typeof configureDeps>;

export default configureDeps;
